import React, { useState, useEffect } from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Box,
  Chip,
  Typography,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Event } from "api/canprospect/event";
import updateEvent from "api/canprospect/updateEvent";
import deleteProspectHistoryEvent from "api/canprospect/deleteProspectHistoryEvent";
import styles from "./events.module.css";
import { Select, MenuItem } from "@mui/material";

interface EventRowProps {
  event: Event;
  fetchEventById: (id: number) => Promise<Event>;
  onDelete: (id: number) => void;
  eventTypes: ProspectHistoryEventType[];
}

interface ProspectHistoryEventType {
  Id: number;
  Type: string;
}

const EventRow: React.FC<EventRowProps> = ({
  event,
  fetchEventById,
  onDelete,
  eventTypes,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editComment, setEditComment] = useState(event.comment);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [rowData, setRowData] = useState<Event>(event);
  const initialEventTypeId =
    eventTypes.find((type) => type.Type === event.eventType)?.Id || 0;
  const [selectedEventType, setSelectedEventType] =
    useState<number>(initialEventTypeId);

    const handleTypeChange = async (newTypeId: number) => {
      setSelectedEventType(newTypeId);
      try {
        await updateEvent(rowData.id, undefined, newTypeId);
        const updatedEvent = await fetchEventById(rowData.id);
        setRowData(updatedEvent);
      } catch (error) {
        console.error("Error updating event type:", error);
      }
    };    

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only update the comment (Notes). Other fields remain unchanged.
      await updateEvent(rowData.id, editComment);
      const updatedEvent = await fetchEventById(rowData.id);
      setRowData(updatedEvent);
      setIsExpanded(false);
    } catch (error) {
      console.error("Error updating notes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProspectHistoryEvent(rowData.id); // Call the delete API
      onDelete(rowData.id); // Notify the parent component about the deletion
      setDeleteModalOpen(false); // Close the modal
    } catch (error) {
      console.error("Error deleting event:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getChipBackgroundColor = (status: string): string => {
    switch (status) {
      case "New Prospect":
        return "#d7f7fc";
      case "Contacted":
        return "#d7fce4";
      case "Replied":
        return "#d7fce4";
      case "Warm Lead":
        return "#f3fcd7";
      case "Qualified Lead":
        return "#fcf6d7";
      case "Proposal Stage":
        return "#fce6d7";
      case "Negotiation Stage":
        return "#fcd7d7";
      case "Closed Won":
        return "#fcd7f1";
      case "Closed Lost":
        return "#e6d7fc";
      default:
        return "inherit";
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell className={styles.tableCell}>
          <Chip
            label={rowData.prospect}
            variant="outlined"
            style={{
              backgroundColor: getChipBackgroundColor(rowData.prospect),
            }}
          />
        </TableCell>
        <TableCell className={styles.tableCell}>
          <Select
            value={selectedEventType}
            onChange={(e) => handleTypeChange(Number(e.target.value))}
            size="small"
          >
            {eventTypes.map((type: ProspectHistoryEventType) => (
              <MenuItem key={type.Id} value={type.Id}>
                {type.Type}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>
          <Typography
            className={styles.commentCell}
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {rowData.comment}
          </Typography>
        </TableCell>
        <TableCell className={styles.tableCell}>{rowData.date}</TableCell>
        <TableCell className={styles.tableCell}>{rowData.time}</TableCell>
        <TableCell className={styles.tableCell}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete
          </Button>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={7}>
            <Box
              display="flex"
              flexDirection="column"
              gap="0.5rem"
              padding="1rem"
            >
              <TextField
                label="Comment"
                multiline
                fullWidth
                rows={4}
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
              />
              <Box
                display="flex"
                justifyContent="flex-start"
                gap="1rem"
                mt={"1rem"}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  style={{ minWidth: "100px" }}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setIsExpanded(false)}
                  style={{ minWidth: "100px" }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </Box>
              {isSaving && <LinearProgress />}{" "}
              {/* Show progress bar when saving */}
            </Box>
          </TableCell>
        </TableRow>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Delete Prospect</DialogTitle>
        <DialogContent>
          Are you sure you would like to delete this event?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            color="primary"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="outlined"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventRow;
